import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ASSETS } from "../data";

const LINKS = [
  { label: "Discover", to: "/#discover" },
  { label: "Broadcast", to: "/#broadcast" },
  { label: "Bounties", to: "/#bounties" },
  { label: "FAQ", to: "/#faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = pathname === "/";

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-[100] px-4 sm:px-6 lg:px-10 pt-4"
      data-testid="navbar"
    >
      <div
        className={`mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 sm:px-6 h-16 transition-all duration-500 ${
          scrolled ? "glass shadow-[0_8px_40px_rgba(0,0,0,0.5)]" : "bg-transparent"
        }`}
      >
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-home-link">
          <img src={ASSETS.icon} alt="MapCast" className="w-9 h-9 rounded-[0.7rem] object-cover ring-1 ring-white/15 group-hover:scale-105 transition-transform duration-300" />
          <span className="font-display font-extrabold text-xl tracking-tight text-white">MapCast</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {isHome
            ? LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.to.replace("/", "")}
                  className="text-sm font-medium text-mc-muted hover:text-white transition-colors duration-300"
                  data-testid={`nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))
            : LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="text-sm font-medium text-mc-muted hover:text-white transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
          <Link to="/investors" className="text-sm font-medium text-mc-muted hover:text-mc-pink transition-colors duration-300" data-testid="nav-investors">Investors</Link>
        </nav>

        <div className="flex items-center gap-3">
          <span
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white/8 border border-white/15 text-white/80 text-sm font-semibold px-5 h-10 cursor-default select-none"
            data-testid="nav-live-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-mc-live" /> Live now
          </span>
          <button
            className="md:hidden w-10 h-10 rounded-full glass flex items-center justify-center text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-auto max-w-7xl mt-2 glass rounded-3xl p-6 flex flex-col gap-4"
            data-testid="mobile-menu"
          >
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.to}
                onClick={() => setOpen(false)}
                className="text-lg font-display font-semibold text-white/90"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/investors"
              onClick={() => setOpen(false)}
              className="text-lg font-display font-semibold text-mc-pink"
              data-testid="mobile-nav-investors"
            >
              Investors
            </Link>
            <span
              className="mt-2 inline-flex justify-center items-center gap-2 rounded-full bg-white/8 border border-white/15 text-white/80 font-semibold h-12 cursor-default select-none"
              data-testid="mobile-live-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-mc-live" /> Live now
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
